import React, { useCallback, useEffect, useState } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { Dropdown } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { App, Article, Main } from '../components/content';
import Sidebar from './Sidebar';
import { findNavItem } from './navConfig';
import ProfileCard from '../components/cards/ProfileCard';
import { useAuth } from '../context/useAuth';
import shell from './shell.module.scss';
import Users from '../features/users/pages/UsersPage';
import Therapies from '../features/therapies/pages/TherapiesPage';
import DailyActivitiesPage from '../features/therapies/pages/DailyActivitiesPage';
import ReservedTherapiesPage from '../features/therapies/pages/ReservedTherapiesPage';
import SavedTherapiesPage from '../features/therapies/pages/SavedTherapiesPage';
import Medications from '../features/medications/pages/MedicationsPage';
import Contacts from '../features/contacts/pages/ContactsPage';
import Senders from '../features/senders/pages/SendersPage';
import TherapyOccurrenceRequestsPage from '../features/therapyOccurrenceRequests/pages/TherapyOccurrenceRequestsPage';
import UserActivityLogsPage from '../features/users/pages/UserActivityLogsPage';
import MessagesPage from '../features/chat/pages/MessagesPage';
import AdminsPage from '../features/admins/pages/AdminsPage';
import { formatUsername } from '../shared/utils/roles';

const COLLAPSE_KEY = 'sidebarCollapsed';

const Layout = () => {
  const location = useLocation();
  const { user } = useAuth();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // ignore storage errors (private mode itd.)
      }
      return next;
    });
  }, []);

  // Na uskim ekranima podrazumevano skupi meni (samo ikonice).
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setCollapsed(true);
    }
  }, []);

  // Ime i rola pored avatara u topbaru. Firme prikazuju responsibleName, ostali
  // ime i prezime (fallback na @korisnicko ime admina, pa email). Rola se
  // prevodi na srpski.
  const displayName =
    (user?.type === 'company'
      ? user?.responsibleName
      : `${user?.firstName || ''} ${user?.lastName || ''}`.trim()) ||
    formatUsername(user?.username) ||
    user?.email ||
    '';
  const roleLabels = {
    superadmin: 'Super admin',
    admin: 'Administrator',
    pharmacy: 'Apoteka',
    manager: 'Menadžer',
    user: 'Korisnik',
  };
  const roleLabel = roleLabels[user?.role] || user?.role || '';

  // Ikonica + naziv aktivne stranice u topbaru (levo).
  const activeNav = findNavItem(location.pathname);
  const ActiveIcon = activeNav?.icon;

  return (
    <App className="app">
      <Sidebar collapsed={collapsed} />
      {/* Moderni round toggle na spoju aside nav-a i topbar-a - preklapa oba.
          Renderuje se na nivou .app (ne u .main) da ga overflow:hidden ne odseca. */}
      <button
        type="button"
        className={`${shell.collapseFloat} ${
          collapsed ? shell.collapseFloatCollapsed : ''
        }`}
        onClick={toggleCollapsed}
        aria-label={collapsed ? 'Proširi meni' : 'Skupi meni'}
        title={collapsed ? 'Proširi meni' : 'Skupi meni'}
      >
        {collapsed ? <RightOutlined /> : <LeftOutlined />}
      </button>
      <Main className="main">
        <div className={shell.topbar}>
          {activeNav && (
            <div className={shell.topbarHeading}>
              {ActiveIcon && (
                <span className={shell.topbarHeadingIcon}>
                  <ActiveIcon />
                </span>
              )}
              <span className={shell.topbarHeadingText}>{activeNav.label}</span>
            </div>
          )}
          <div className={shell.topbarSpacer} />
          <Dropdown
            open={isDropdownVisible}
            onOpenChange={(visible) => setIsDropdownVisible(visible)}
            trigger={['click']}
            popupRender={() => (
              <ProfileCard onOpenModal={() => setIsDropdownVisible(false)} />
            )}
          >
            <div className={shell.profileTrigger}>
              <div className={shell.profileMeta}>
                <span className={shell.profileName}>{displayName}</span>
                {roleLabel && (
                  <span className={shell.profileRole}>{roleLabel}</span>
                )}
              </div>
              <div
                className={shell.avatar}
                style={{ padding: user?.profileImage ? 0 : 8 }}
              >
                <img
                  src={user?.profileImage || '/assets/icons/user.svg'}
                  alt="user_icon"
                />
              </div>
            </div>
          </Dropdown>
        </div>
        <Article key={location.pathname} className="page-transition">
          <Routes>
            <Route path="/terapije" element={<Therapies />} />
            <Route
              path="/rezervisane-terapije"
              element={<ReservedTherapiesPage />}
            />
            <Route path="/sacuvane-terapije" element={<SavedTherapiesPage />} />
            <Route
              path="/istorija-dnevnih-aktivnosti"
              element={<DailyActivitiesPage />}
            />
            <Route path="/korisnicke-izmene" element={<UserActivityLogsPage />} />
            <Route path="/zahtevi" element={<TherapyOccurrenceRequestsPage />} />
            <Route path="/" element={<Navigate to="/terapije" replace />} />
            <Route path="/korisnici" element={<Users />} />
            <Route path="/poruke" element={<MessagesPage />} />
            <Route path="/lekovi" element={<Medications />} />
            <Route path="/kontakti" element={<Contacts />} />
            <Route path="/posiljaoci" element={<Senders />} />
            <Route path="/administratori" element={<AdminsPage />} />
          </Routes>
        </Article>
      </Main>
    </App>
  );
};

export default Layout;
